<template>
  <StepLayout
    :keyword="keyword"
    title="他にも気になることがあれば教えてください"
    :current-step="4"
    optional
    @back="handleBack"
  >
    <div class="step-free-text">
      <FreeTextInput
        v-model="model"
        class="step-free-text-input"
        :placeholder="placeholder"
        aria-describedby="step-free-text-description"
      />

      <p id="step-free-text-description" class="step-free-text__hint">
        入力は任意ですが、詳しく入力すると検索精度が上がります
      </p>

      <!-- 年代・性別セレクター -->
      <div class="step-free-text__selectors">
        <div class="step-free-text__selector-group">
          <label class="step-free-text__label"
            >年代
            <span class="step-free-text__optional-text">（任意）</span></label
          >
          <div class="select-wrap">
            <select v-model="ageModel">
              <option
                v-for="option in ageSelectOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="step-free-text__selector-group">
          <label class="step-free-text__label"
            >性別
            <span class="step-free-text__optional-text">（任意）</span></label
          >
          <div class="select-wrap">
            <select v-model="genderModel">
              <option
                v-for="option in genderSelectOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        class="button button--primary button--full button--lg step-free-text__search-button"
        @click="handleSearch"
      >
        相談を見つける（無料）
        <i class="material-icons" aria-hidden="true">search</i>
      </button>
    </template>
  </StepLayout>
</template>

<script setup lang="ts">
import StepLayout from './StepLayoutInline.vue';
import FreeTextInput from '../FreeTextInput.vue';
import { ageSelectOptions, genderSelectOptions } from '../../questions';
import type { TargetAge, TargetGender } from '../../types';

defineProps<{
  keyword: string;
}>();

const model = defineModel<string>({ default: '' });
const ageModel = defineModel<TargetAge>('age', { default: 'NOT_SELECTED' });
const genderModel = defineModel<TargetGender>('gender', { default: 'NOT_SELECTED' });

const emit = defineEmits<{
  search: [];
  back: [];
}>();

const placeholder = `例）朝起きたときに症状が一番辛くなり、ズキズキと痛みます
例）健康診断では肝臓の数値が良くないと言われています`;

const handleSearch = () => {
  emit('search');
};

const handleBack = () => {
  emit('back');
};
</script>

<style lang="scss" scoped>
@import '~green/_variables.scss';

.step-free-text {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__hint {
    font-size: 0.875rem;
    color: #d32f2f;
    margin: 0 auto;
    max-width: 90%;
  }

  &__search-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .material-icons {
      font-size: 1.875rem;
    }
  }

  &__selectors {
    display: flex;
    gap: 16px;
    margin-top: 24px;
    flex-wrap: wrap;
    width: 100%;
  }

  &__selector-group {
    flex: 1;
    min-width: 140px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__label {
    font-size: 14px;
    font-weight: bold;
    color: $base-font-color;
  }

  &__optional-text {
    font-size: 12px;
    font-weight: normal;
    color: #999;
  }
}

.select-wrap {
  position: relative;

  select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    font-size: 14px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fff;
    appearance: none;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover {
      border-color: #0ba780;
    }

    &:focus {
      outline: none;
      border-color: #0ba780;
      box-shadow: 0 0 0 3px rgba(11, 167, 128, 0.1);
    }
  }

  &::after {
    content: '';
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #666;
    pointer-events: none;
  }
}

.step-free-text-input {
  :deep(.textarea) {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    resize: vertical;
    min-height: 15rem;
    font-family: inherit;

    &::placeholder {
      color: $quaternary-font-color;
    }

    &:focus {
      outline: none;
      border-color: $primary-color;
      box-shadow: 0 0 0 2px rgba($primary-color, 0.2);
    }
  }

  :deep(.counter) {
    text-align: right;
    font-size: 0.875rem;
    color: $tertiary-color;
  }
}
</style>
